import { useEffect, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { useGamePortalPagesState } from 'state/hooks/game-portal-pages.hook';
import {
  EBlockType,
  IBlockContentProps,
  IBlockItem,
  ICtaItem,
  IImageItem,
  IRegularBlock,
  IVideoItem
} from 'views/Pages/PagesForm/constants';
import { pageFormSchema } from 'views/Pages/PagesForm/validation-schema';
import { z } from 'zod';

import Button from 'components/Button/Button';

import { ItemCard } from './ItemCard';

export const ItemsList: React.FC<IBlockContentProps> = ({
  block,
  blockIndex
}) => {
  const { watch, setValue, trigger } =
    useFormContext<z.infer<typeof pageFormSchema>>();
  const { setOpenItemIndex } = useGamePortalPagesState();

  const regularBlock = useMemo(() => block as IRegularBlock, [block]);

  const items = useMemo(() => regularBlock.items || [], [regularBlock?.items]);

  const hasItems = useMemo(
    () => items?.length && items.length > 0,
    [items?.length]
  );

  useEffect(() => {
    const triggerBlocks = async () => {
      await trigger(`blocksV2`);
    };
    triggerBlocks();
  }, [items?.length]);

  const createEmptyItem = (): IBlockItem => {
    const blockType = regularBlock.type as EBlockType;

    switch (blockType) {
      case EBlockType.IMAGE:
        return {
          type: EBlockType.IMAGE,
          url: ''
        } as IImageItem;
      case EBlockType.VIDEO:
        return {
          type: EBlockType.VIDEO,
          url: ''
        } as IVideoItem;
      case EBlockType.CTA:
        return {
          type: EBlockType.CTA,
          text: '',
          redirectUrl: '',
          assetUrl: '',
          assetUrlMobile: ''
        } as ICtaItem;
      default:
        return {
          type: EBlockType.CTA,
          text: '',
          redirectUrl: ''
        } as ICtaItem;
    }
  };

  const handleAddItem = async () => {
    const formBlocks = watch('blocksV2') as IRegularBlock[];
    const currentBlock = formBlocks[blockIndex];
    const currentItems = currentBlock.items || [];
    const newItem: IBlockItem = createEmptyItem();
    const newItems = [...currentItems, newItem];

    const updatedBlocks = [...formBlocks];
    updatedBlocks[blockIndex] = {
      ...currentBlock,
      items: newItems
    };

    setValue('blocksV2', updatedBlocks, {
      shouldValidate: true,
      shouldDirty: true
    });

    const newItemIndex = newItems.length - 1;
    setOpenItemIndex(`${blockIndex}-${newItemIndex}`);
  };

  return (
    <>
      {hasItems
        ? items.map((item: IBlockItem, itemIndex: number) => (
            <ItemCard
              item={item}
              index={itemIndex}
              blockIndex={blockIndex}
              block={regularBlock}
              key={itemIndex}
            />
          ))
        : null}
      <Button
        onClick={handleAddItem}
        variant="secondary"
        type="button"
        className="text-sm w-full text-base-secondaryForeground"
        leftIcon={<FontAwesomeIcon icon="fa-light fa-circle-plus" />}
      >
        Add Item
      </Button>
    </>
  );
};
